import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Serviço de Data Binding - Sistema de Injeção e Sincronização de Dados
 * 
 * Responsável por:
 * 1. Armazenar dados recebidos do backend/mock
 * 2. Resolver JSON paths (ex: "customer.name", "items[0].price")
 * 3. Aplicar transformações aos valores (ex: uppercase, currency, date)
 * 4. Fornecer observables para componentes reagirem a mudanças de dados
 * 5. Atualizar valores e sincronizar mudanças
 */
@Injectable()
export class DataBindingService {
  private dataStore$ = new BehaviorSubject<any>(null);
  
  /**
   * Define os dados que serão usados para binding
   * @param data Dados a serem armazenados (geralmente vêm do backend)
   */
  setData(data: any): void {
    // ✅ CORREÇÃO: Sanitizar dados para evitar undefined em campos numéricos
    const sanitizedData = this.sanitizeData(data);
    this.dataStore$.next(sanitizedData);
  }
  
  /**
   * Sanitiza dados recursivamente, convertendo undefined para null
   * Isso evita erros em componentes como nz-input-number que não lidam bem com undefined
   */
  private sanitizeData(data: any): any {
    if (data === undefined) {
      return null;
    }
    
    if (data === null || typeof data !== 'object') {
      return data;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }
    
    const sanitized: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        sanitized[key] = this.sanitizeData(data[key]);
      }
    }
    return sanitized;
  }
  
  /**
   * Atualiza um valor específico no JSON usando path
   * @param path Caminho JSON (ex: "response.item_list[0].item_name")
   * @param value Novo valor
   */
  updateValue(path: string, value: any): void {
    const currentData = this.dataStore$.value;
    
    
    if (!currentData || !path) {    
      return;
    }
    
    try {
      const updatedData = JSON.parse(JSON.stringify(currentData));
      
      this.setValueByPath(updatedData, path, value);
      
      this.dataStore$.next(updatedData);
      
    } catch (error) {
      console.error(`❌ [updateValue] Erro ao atualizar path "${path}":`, error);
    }
  }
  
  /**
   * Define um valor em um path específico (suporta arrays e objetos)
   */
  private setValueByPath(obj: any, path: string, value: any): void {
    const parts = path.split(/[\.\[\]]/).filter(p => p);
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      
      if (current[part] === undefined || current[part] === null || typeof current[part] !== 'object') {
        // Criar objeto ou array baseado no próximo índice
        // Também sobrescreve primitivos (string/number/boolean) que bloqueiam paths aninhados
        const nextPart = parts[i + 1];
        current[part] = /^\d+$/.test(nextPart) ? [] : {};
      }
      
      current = current[part];
    }
    
    // Definir o valor final
    const lastPart = parts[parts.length - 1];
    current[lastPart] = value;
  }
  
  /**
   * Obtém observable dos dados
   * Componentes podem se inscrever para reagir a mudanças
   */
  getData$(): Observable<any> {
    return this.dataStore$.asObservable();
  }
  
  /**
   * Obtém dados atuais (snapshot)
   */
  getCurrentData(): any {
    return this.dataStore$.value;
  }
  
  /**
   * Resolve um JSON path nos dados
   * 
   * Suporta:
   * - Notação de ponto: "customer.address.city"
   * - Arrays: "items[0].name"
   * - Aninhamento: "order.items[0].product.name"
   * 
   * @param data Objeto de dados
   * @param path Caminho JSON (ex: "customer.name", "items[0].price")
   * @returns Valor encontrado ou null se não existir (NUNCA undefined)
   */
  resolvePath(data: any, path: string): any {
    if (!data || !path) return null;
    
    try {
      // Suporta notação de ponto e arrays
      // Split por . [ ] e remove strings vazias
      const parts = path.split(/[\.\[\]]/).filter(p => p);
      let result = data;
      
      for (const part of parts) {
        if (result === null || result === undefined) return null;
        result = result[part];
      }
      
      // ✅ CORREÇÃO CRÍTICA: NUNCA retornar undefined, sempre null
      // Isso evita erro "Cannot read properties of undefined (reading 'toString')" em nz-input-number
      return result === undefined ? null : result;
    } catch (error) {
      console.warn(`Erro ao resolver path "${path}":`, error);
      return null;
    }
  }
  
  /**
   * Resolve path com transformação opcional
   * @param data Objeto de dados
   * @param path Caminho JSON
   * @param transformFn Nome da função de transformação
   * @returns Valor transformado
   */
  resolvePathWithTransform(data: any, path: string, transformFn?: string): any {
    let value = this.resolvePath(data, path);
    
    if (transformFn && value !== null && value !== undefined) {
      value = this.applyTransform(value, transformFn);
    }
    
    return value;
  }
  
  /**
   * Aplica transformação a um valor
   * @param value Valor a ser transformado
   * @param transformFn Nome da função de transformação
   * @returns Valor transformado
   */
  private applyTransform(value: any, transformFn: string): any {
    switch (transformFn.toLowerCase()) {
      case 'uppercase':
        return String(value).toUpperCase();
        
      case 'lowercase':
        return String(value).toLowerCase();
        
      case 'currency':
        return this.formatCurrency(value);
        
      case 'date':
        return this.formatDate(value);
        
      case 'datetime':
        return this.formatDateTime(value);
        
      case 'percent':
        return this.formatPercent(value);
        
      case 'number':
        return this.formatNumber(value);
        
      default:
        console.warn(`Função de transformação desconhecida: ${transformFn}`);
        return value;
    }
  }
  
  /**
   * Formata valor como moeda brasileira
   */
  private formatCurrency(value: any): string {
    const numValue = Number(value);
    if (isNaN(numValue)) return value;
    
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(numValue);
  }
  
  /**
   * Formata valor como data
   */
  private formatDate(value: any): string {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      
      return date.toLocaleDateString('pt-BR');
    } catch {
      return value;
    }
  }
  
  /**
   * Formata valor como data e hora
   */
  private formatDateTime(value: any): string {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      
      return date.toLocaleString('pt-BR');
    } catch {
      return value;
    }
  }
  
  /**
   * Formata valor como porcentagem
   */
  private formatPercent(value: any): string {
    const numValue = Number(value);
    if (isNaN(numValue)) return value;
    
    return new Intl.NumberFormat('pt-BR', { 
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue / 100);
  }
  
  /**
   * Formata valor como número
   */
  private formatNumber(value: any): string {
    const numValue = Number(value);
    if (isNaN(numValue)) return value;
    
    return new Intl.NumberFormat('pt-BR').format(numValue);
  }
  
  /**
   * Valida se um path é válido nos dados atuais
   * @param path Caminho JSON a ser validado
   * @returns true se o path existe e tem valor válido
   */
  validatePath(path: string): boolean {
    const data = this.dataStore$.value;
    if (!data) return false;
    
    const value = this.resolvePath(data, path);
    return value !== null && value !== undefined;
  }
  
  /**
   * Retorna preview de um valor para um path específico
   * Útil para mostrar no inspetor ao configurar paths
   * @param path Caminho JSON
   * @returns String com preview do valor ou mensagem de erro
   */
  getPathPreview(path: string): string {
    if (!path) return '';
    
    const data = this.dataStore$.value;
    if (!data) return '(Sem dados carregados)';
    
    const value = this.resolvePath(data, path);
    
    if (value === null || value === undefined) {
      return '(Path não encontrado)';
    }
    
    // Limitar tamanho do preview
    const preview = JSON.stringify(value);
    return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
  }
  
  /**
   * Limpa os dados armazenados
   */
  clearData(): void {
    this.dataStore$.next(null);
  }
  
  /**
   * Atualiza um item inteiro em um array
   * @param arrayPath Caminho do array (ex: "response.item_list")
   * @param itemIndex Índice do item no array
   * @param updatedItem Item atualizado
   */
  updateArrayItem(arrayPath: string, itemIndex: number, updatedItem: any): void {
    const currentData = this.dataStore$.value;
    if (!currentData) return;
    
    try {
      const updatedData = JSON.parse(JSON.stringify(currentData));
      const array = this.resolvePath(updatedData, arrayPath);
      
      if (Array.isArray(array) && itemIndex >= 0 && itemIndex < array.length) {
        array[itemIndex] = updatedItem;
        this.dataStore$.next(updatedData);
      } else {
        console.warn(`Índice ${itemIndex} inválido para array em "${arrayPath}"`);
      }
    } catch (error) {
      console.error(`Erro ao atualizar item do array:`, error);
    }
  }
  
  /**
   * Atualiza uma propriedade específica de um item em um array
   * @param arrayPath Caminho do array
   * @param itemIndex Índice do item
   * @param propertyPath Caminho da propriedade dentro do item (ex: "brand.brand_id")
   * @param value Novo valor
   */
  updateArrayItemProperty(arrayPath: string, itemIndex: number, propertyPath: string, value: any): void {
    const fullPath = `${arrayPath}[${itemIndex}].${propertyPath}`;
    
    this.updateValue(fullPath, value);
    
  }
  
  /**
   * Obtém o JSON completo formatado
   * @returns String JSON formatada
   */
  getFormattedJson(): string {
    const data = this.dataStore$.value;
    return JSON.stringify(data, null, 2);
  }
  
  /**
   * Obtém resumo das alterações (quantidade de itens em arrays)
   */
  getDataSummary(): any {
    const data = this.dataStore$.value;
    if (!data) return null;
    
    const summary: any = {
      hasData: true,
      structure: {}
    };
    
    // Analisar estrutura recursivamente
    this.analyzeStructure(data, summary.structure);
    
    return summary;
  }
  
  private analyzeStructure(obj: any, result: any, prefix: string = ''): void {
    if (!obj || typeof obj !== 'object') return;
    
    Object.keys(obj).forEach(key => {
      const path = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];
      
      if (Array.isArray(value)) {
        result[path] = `Array(${value.length} items)`;
      } else if (typeof value === 'object' && value !== null) {
        this.analyzeStructure(value, result, path);
      }
    });
  }
  
  /**
   * Adiciona um novo item em um array
   * @param arrayPath Caminho do array (ex: "response.item_list")
   * @param newItem Item a ser adicionado (ou template vazio se não fornecido)
   */
  addArrayItem(arrayPath: string, newItem?: any, columnConfig?: any[], defaultValues?: Record<string, any>): void {
    let currentData = this.dataStore$.value;
    
    // ⭐ NOVO: Se não há dados, criar estrutura vazia baseada no path
    if (!currentData) {
      
      currentData = {};
      this.dataStore$.next(currentData);
    }
    
    try {
      const updatedData = JSON.parse(JSON.stringify(currentData));
      let resolved = this.resolvePath(updatedData, arrayPath);
      
      // ⭐ NOVO: Se path não existe (null), criar a estrutura automaticamente
      if (resolved === null) {
        
        resolved = this.createPathStructure(updatedData, arrayPath);
      }
      
      // ⭐ NOVO: Se não for array, tentar encontrar o primeiro array dentro do objeto
      if (!Array.isArray(resolved) && resolved && typeof resolved === 'object') {
        
        const arrayKey = this.findFirstArrayKey(resolved);
        if (arrayKey) {
        
          resolved = resolved[arrayKey];
          // Atualizar arrayPath para incluir o array encontrado
          arrayPath = `${arrayPath}.${arrayKey}`;
        } else {
          // Não tem array dentro do objeto, criar um array padrão
        
          const defaultArrayKey = this.guessArrayKeyFromPath(arrayPath);
          resolved[defaultArrayKey] = [];
          resolved = resolved[defaultArrayKey];
          arrayPath = `${arrayPath}.${defaultArrayKey}`;
        
        }
      }
      
      const array = resolved;
      
      if (Array.isArray(array)) {
        let itemToAdd = newItem;
        
        if (!itemToAdd) {
          if (array.length > 0) {
            // Tem items → copiar estrutura do primeiro
        
            itemToAdd = this.createEmptyItem(array[0]);
          } else if (columnConfig && columnConfig.length > 0) {
            // Array vazio → criar baseado nas colunas configuradas
        
            itemToAdd = this.createItemFromColumns(columnConfig);
          } else {
            // Fallback: objeto com apenas ID
            console.warn('   Array vazio sem configuração de colunas - criando item básico');
            itemToAdd = { 
              id: `new-${Date.now()}`,
              createdAt: new Date().toISOString()
            };
          }
        }
        
        
        // Aplicar defaultValues dos campos do formulário ao item criado
        if (defaultValues && typeof defaultValues === 'object') {
          Object.keys(defaultValues).forEach(fieldPath => {
            const val = defaultValues[fieldPath];
            if (val !== undefined && val !== null && val !== '') {
              if (fieldPath.includes('.')) {
                this.setValueByPath(itemToAdd, fieldPath, val);
              } else {
                itemToAdd[fieldPath] = val;
              }
            }
          });
        }

        array.push(itemToAdd);
        this.dataStore$.next(updatedData);
      } else {
        const value = this.resolvePath(updatedData, arrayPath);
        console.error(`❌ Path "${arrayPath}" não resolve para array`);
        console.error(`   Tipo encontrado: ${Array.isArray(value) ? 'Array' : typeof value}`);
        console.error(`   Valor:`, value);
        console.warn(`   💡 Dica: Verifique o dataPath da grade no Inspector. O path deve apontar para um array, não para um objeto.`);
      }
    } catch (error) {
      console.error(`Erro ao adicionar item no array:`, error);
    }
  }
  
  /**
   * Remove um item de um array
   * @param arrayPath Caminho do array (ex: "response.item_list")
   * @param itemIndex Índice do item a ser removido
   */
  removeArrayItem(arrayPath: string, itemIndex: number): void {
    const currentData = this.dataStore$.value;
    if (!currentData) {
      console.warn('Nenhum dado carregado para remover item');
      return;
    }
    
    try {
      const updatedData = JSON.parse(JSON.stringify(currentData));
      const array = this.resolvePath(updatedData, arrayPath);
      
      if (Array.isArray(array) && itemIndex >= 0 && itemIndex < array.length) {
        array.splice(itemIndex, 1);
        this.dataStore$.next(updatedData);
      } else {
        console.warn(`Índice ${itemIndex} inválido para array em "${arrayPath}" (length: ${array?.length || 0})`);
      }
    } catch (error) {
      console.error(`Erro ao remover item do array:`, error);
    }
  }
  
  /**
   * Cria um item vazio baseado na estrutura de outro item
   * @param templateItem Item usado como template
   * @returns Novo item com valores vazios/padrão
   */
  private createEmptyItem(templateItem: any): any {
    if (!templateItem) return {};
    
    const emptyItem: any = {};
    
    for (const key in templateItem) {
      const value = templateItem[key];
      
      if (typeof value === 'string') {
        emptyItem[key] = '';
      } else if (typeof value === 'number') {
        // CORREÇÃO: Usar null em vez de 0 para campos numéricos.
        // O valor 0 é um valor de negócio válido (ex: quantidade=0) e confuso quando
        // usado como placeholder em campos de referência (ex: CODECOMPANY, CODECONTRACT).
        // Null indica claramente "sem valor" e os campos de pesquisa ficam vazios.
        emptyItem[key] = null;
      } else if (typeof value === 'boolean') {
        emptyItem[key] = false;
      } else if (Array.isArray(value)) {
        emptyItem[key] = [];
      } else if (typeof value === 'object' && value !== null) {
        emptyItem[key] = this.createEmptyItem(value); // Recursivo para objetos aninhados
      } else {
        emptyItem[key] = null;
      }
    }
    
    return emptyItem;
  }
  
  /**
   * Cria um item baseado na configuração das colunas
   * @param columns Configuração das colunas da grade
   * @returns Novo item com campos das colunas
   */
  private createItemFromColumns(columns: any[]): any {
    const item: any = {};
    
    columns.forEach(col => {
      const fieldName = col.dataPath || col.field;
      if (fieldName) {
        const defaultValue = this.getDefaultValueForColumnType(col.type);
        if (fieldName.includes('.')) {
          // Expandir paths com ponto para objetos aninhados
          // Ex: "extras.myName" → { extras: { myName: "" } }
          this.setValueByPath(item, fieldName, defaultValue);
        } else {
          item[fieldName] = defaultValue;
        }
      }
    });
    
    return item;
  }
  
  /**
   * Retorna valor padrão baseado no tipo da coluna
   * @param type Tipo da coluna
   * @returns Valor padrão apropriado
   */
  private getDefaultValueForColumnType(type: string): any {
    // Normalizar tipo (lowercase e remover espaços)
    const normalizedType = (type || '').toLowerCase().trim();
    
    switch (normalizedType) {
      case 'number':
      case 'numeric':
      case 'integer':
      case 'float':
      case 'decimal':
        return null; // ✅ Usar null para campos numéricos vazios
      case 'boolean':
      case 'switch':
      case 'checkbox':
        return false;
      case 'date':
      case 'datetime':
        return null; // ✅ Usar null para datas vazias
      case 'array':
        return [];
      case 'object':
        return {};
      case 'text':
      case 'string':
      default:
        return ''; // String vazia para campos de texto
    }
  }
  
  /**
   * Encontra a primeira chave que contém um array dentro de um objeto
   * @param obj Objeto para procurar
   * @returns Nome da chave que contém o array, ou null
   */
  private findFirstArrayKey(obj: any): string | null {
    if (!obj || typeof obj !== 'object') return null;
    
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        return key;
      }
    }
    
    return null;
  }
  
  /**
   * Cria a estrutura de objetos necessária para um path
   * @param data Objeto raiz
   * @param path Path a ser criado
   * @returns Array criado no final do path
   */
  private createPathStructure(data: any, path: string): any[] {
    const parts = path.split(/[\.\[\]]/).filter(p => p);
    let current = data;
    
    // Criar objetos intermediários
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      
      if (!current[part]) {
        // Se é a última parte, criar array
        if (i === parts.length - 1) {
          current[part] = [];
        } else {
          // Se não, criar objeto
          current[part] = {};
        }
      }
      
      current = current[part];
    }
    
    // Se a última parte não é array, tentar adicionar array padrão
    if (!Array.isArray(current)) {
      const arrayKey = this.guessArrayKeyFromPath(path);
      if (!current[arrayKey]) {
        current[arrayKey] = [];
      }
      return current[arrayKey];
    }
    
    return current;
  }
  
  /**
   * Tenta adivinhar o nome do array baseado no path
   * Ex: "response.magalu" -> "item_list"
   */
  private guessArrayKeyFromPath(path: string): string {
    // Nomes comuns de arrays em APIs
    const commonArrayNames = ['items', 'item_list', 'list', 'data', 'records', 'results'];
    
    // Se o path termina com algo que sugere um array (plural), usar esse nome
    const lastPart = path.split('.').pop() || '';
    if (lastPart.endsWith('s') || lastPart.includes('list')) {
      return lastPart;
    }
    
    // Padrão: usar "item_list"
    return 'item_list';
  }
}



